import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Redo,
  Undo,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";

export const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({
        placeholder: "Write your content here...",
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[200px]",
      },
    },
  });

  useEffect(() => {
    if (editor && content && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div className="border rounded bg-white shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-2 border-b bg-gray-50">
        {/* Text Formatting */}
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("bold") ? "bg-gray-200 font-bold" : ""}`}>
          <Bold size={16} />
        </button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("italic") ? "bg-gray-200 italic" : ""}`}>
          <Italic size={16} />
        </button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("underline") ? "bg-gray-200 underline" : ""}`}>
          <UnderlineIcon size={16} />
        </button>

        {/* Lists */}
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("bulletList") ? "bg-gray-200" : ""}`}>
          <List size={16} />
        </button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("orderedList") ? "bg-gray-200" : ""}`}>
          <ListOrdered size={16} />
        </button>

        {/* Headings */}
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""}`}>
          H1
        </button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""}`}>
          H2
        </button>

        {/* Alignment */}
        <button onClick={() => editor.chain().focus().setTextAlign("left").run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: "left" }) ? "bg-gray-200" : ""}`}>
          <AlignLeft size={16} />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign("center").run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: "center" }) ? "bg-gray-200" : ""}`}>
          <AlignCenter size={16} />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign("right").run()} className={`p-1 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: "right" }) ? "bg-gray-200" : ""}`}>
          <AlignRight size={16} />
        </button>

        {/* Undo/Redo */}
        <div className="ml-auto flex gap-2">
          <button onClick={() => editor.chain().focus().undo().run()} className="p-1 rounded hover:bg-gray-200">
            <Undo size={16} />
          </button>
          <button onClick={() => editor.chain().focus().redo().run()} className="p-1 rounded hover:bg-gray-200">
            <Redo size={16} />
          </button>
        </div>
      </div>

      {/* Editor Area */}
      <div className="p-4">
        <div className="min-h-[200px] border border-gray-200 rounded p-3 focus-within:ring-2 focus-within:ring-blue-300">
          <EditorContent
            editor={editor}
            className="[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-2 leading-relaxed"
          />
        </div>
      </div>
    </div>
  );
};
